import React from 'react';
import { TrendingUp } from 'lucide-react';
import { SourceBreakdownData } from '../types';
interface SourceMixProps {
  data: SourceBreakdownData[];
  dateRangeLabel: string;
  onSourceClick?: (source: SourceBreakdownData) => void;
}
const formatCurrency = (n: number) => {
  if (n === 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
export function SourceMix({
  data,
  dateRangeLabel,
  onSourceClick
}: SourceMixProps) {
  const totalFunded = data.reduce((s, x) => s + x.funded, 0);
  const totalFundedAmount = data.reduce((s, x) => s + x.fundedAmount, 0);
  // Sort by funded count desc so biggest source is first
  const sorted = [...data].sort((a, b) => b.funded - a.funded);
  return <div className="space-y-4" data-id="element-315">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-316">
        <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-317">
          <div className="flex items-center gap-2" data-id="element-318">
            <div className="w-1.5 h-5 bg-[#c9a84c] rounded-sm" data-id="element-319"></div>
            <h2 className="text-base font-bold text-[#3d3d3d]" data-id="element-320">
              Deal Mix by Source
            </h2>
            <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md ml-1" data-id="element-321">
              {dateRangeLabel}
            </span>
          </div>
          <div className="text-xs text-gray-500 font-medium" data-id="element-322">
            <span className="font-bold text-[#3d3d3d]" data-id="element-323">{totalFunded}</span>{' '}
            funded deals ·{' '}
            <span className="font-bold text-[#c9a84c]" data-id="element-324">
              {totalFundedAmount > 0 ? `$${totalFundedAmount.toLocaleString()}` : '—'}
            </span>
          </div>
        </div>

        {/* Distribution bar */}
        <div className="p-4 border-b border-gray-100" data-id="element-325">
          <div className="flex h-3 rounded-full overflow-hidden bg-gray-100" data-id="element-326">
            {sorted.filter(s => s.funded > 0).map(source => <div key={source.id} className="h-full transition-all hover:opacity-90" style={{
            width: `${totalFunded > 0 ? source.funded / totalFunded * 100 : 0}%`,
            backgroundColor: source.color
          }} title={`${source.source}: ${source.funded} deals`} data-id="element-327" />)}
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4" data-id="element-328">
          {sorted.map(source => {
          const pct = totalFunded > 0 ? source.funded / totalFunded * 100 : 0;
          return <div key={source.id} onClick={() => onSourceClick?.(source)} className="border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-[#c9a84c]/40 transition-all bg-gray-50/30 cursor-pointer active:scale-[0.99]" data-id="element-329">
                <div className="flex items-center justify-between mb-3" data-id="element-330">
                  <div className="flex items-center gap-2" data-id="element-331">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
                  backgroundColor: source.color
                }} data-id="element-332"></div>
                    <span className="font-bold text-[#3d3d3d] text-sm" data-id="element-333">
                      {source.source}
                    </span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400" data-id="element-334">
                    Source
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-335">
                  <div data-id="element-336">
                    <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-0.5" data-id="element-337">
                      Deals
                    </div>
                    <div className="text-2xl font-bold text-[#3d3d3d] tracking-tight" data-id="element-338">
                      {source.funded}
                    </div>
                    <div className="text-xs font-bold text-[#5a7fa5]" data-id="element-339">
                      {pct.toFixed(1)}% of total
                    </div>
                  </div>
                  <div data-id="element-340">
                    <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-0.5" data-id="element-341">
                      Avg Loan
                    </div>
                    <div className="text-2xl font-bold text-[#c9a84c] tracking-tight" data-id="element-342">
                      {formatCurrency(source.avgAmount)}
                    </div>
                    <div className="text-xs font-medium text-gray-500" data-id="element-343">
                      per deal
                    </div>
                  </div>
                </div>

                {/* Share bar */}
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-344">
                  <div className="h-full rounded-full transition-all" style={{
                width: `${pct}%`,
                backgroundColor: source.color
              }} data-id="element-345"></div>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200" data-id="element-346">
                  <span className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-347">
                    Volume
                  </span>
                  <span className="text-sm font-bold text-[#3d3d3d]" data-id="element-348">
                    {formatCurrency(source.fundedAmount)}
                  </span>
                </div>
              </div>;
        })}
        </div>

        {totalFunded === 0 && <div className="px-4 pb-6 text-center text-sm text-gray-400" data-id="element-349">
            No funded deals in this date range.
          </div>}
      </div>

      {/* Footer insight */}
      {totalFunded > 0 && sorted[0]?.funded > 0 && <div className="bg-[#3d3d3d] text-white rounded-xl p-4 flex items-center gap-3" data-id="element-350">
          <div className="bg-[#c9a84c]/20 p-2 rounded-lg text-[#c9a84c]" data-id="element-351">
            <TrendingUp size={18} data-id="element-352" />
          </div>
          <div className="text-sm" data-id="element-353">
            <span className="font-bold text-[#c9a84c]" data-id="element-354">{sorted[0].source}</span>{' '}
            is your top source —{' '}
            <span className="font-bold" data-id="element-355">
              {(sorted[0].funded / totalFunded * 100).toFixed(1)}%
            </span>{' '}
            of all deals at an avg loan of{' '}
            <span className="font-bold" data-id="element-356">
              {formatCurrency(sorted[0].avgAmount)}
            </span>
            .
          </div>
        </div>}
    </div>;
}